/**
 * ChecklistWidget — persistent activation checklist (top-right of dashboard).
 *
 * SYNTHESIS_2026-05-05 §3.4 — single most-cited UX pattern across the
 * onboarding research. Stays visible until 100% complete or user dismisses,
 * derives state from /api/onboarding/state, and surfaces the next-best
 * action prominently.
 *
 * Cardinal Rule #4: only operational metadata in the recent-events list.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { Check, ChevronDown, ChevronUp, X, FileSignature } from 'lucide-react';
import { useOnboardingState } from '../../../hooks/useAPI';
import { DpaAcceptModal } from './DpaAcceptModal';
import type { OnboardingStep } from '../../../types/api';

const STEP_META: Record<OnboardingStep, {
  title: string;
  description: string;
  href: string;
}> = {
  workspace_created: {
    title: 'Workspace ready',
    description: 'Your audit-trail vault is provisioned.',
    href: '/dashboard',
  },
  agent_created: {
    title: 'Connect your first agent',
    description: 'Install the SDK and register an agent.',
    href: '/dashboard/agents/new',
  },
  trace_received: {
    title: 'Receive a real trace',
    description: 'Send one decision through the SDK to see the audit chain extend.',
    href: '/dashboard/agents/new',
  },
  policy_created: {
    title: 'Configure a policy',
    description: 'Define what should be blocked, flagged, or routed for review.',
    href: '/dashboard/policies',
  },
  export_downloaded: {
    title: 'Export an audit bundle',
    description: 'Generate a verifiable PDF / hash-chain export for an auditor.',
    href: '/dashboard/activity-log',
  },
};

const DISMISS_KEY = 'adjudon_checklist_dismissed_v1';

// Module-level so the array isn't reallocated each render. The order
// must match the checklist's intended progression (matches STEPS in
// backend/services/onboardingService.js).
const ALL_STEPS: OnboardingStep[] = [
  'workspace_created', 'agent_created', 'trace_received',
  'policy_created', 'export_downloaded',
];

export function ChecklistWidget() {
  const { data: state, isLoading } = useOnboardingState();
  const [expanded, setExpanded] = useState(true);
  const [dpaModalOpen, setDpaModalOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (dismissed || isLoading || !state) return null;
  if (state.progress.percent === 100 && state.dpaAccepted) return null;

  const done = new Set(state.completedSteps);
  const nextStep = ALL_STEPS.find((s) => !done.has(s));

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <>
    <DpaAcceptModal
      isOpen={dpaModalOpen}
      onClose={() => setDpaModalOpen(false)}
    />
    <aside
      className="adp-card border border-border bg-surface-1 w-80 max-w-[calc(100vw-2rem)]"
      role="complementary"
      aria-label={`Onboarding checklist — ${state.progress.completed} of ${state.progress.total} steps complete`}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground">
              Activation
            </span>
            <span className="text-sm font-semibold text-foreground truncate">
              {state.progress.completed} / {state.progress.total} complete
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="p-2 min-h-[32px] min-w-[32px] flex items-center justify-center hover:bg-surface-2"
            aria-label={expanded ? 'Collapse checklist' : 'Expand checklist'}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp className="size-4" aria-hidden="true" /> : <ChevronDown className="size-4" aria-hidden="true" />}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-2 min-h-[32px] min-w-[32px] flex items-center justify-center hover:bg-surface-2"
            aria-label="Dismiss checklist permanently"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Progress bar — semantic */}
      <div
        role="progressbar"
        aria-valuenow={state.progress.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Activation progress"
        className="h-0.5 bg-surface-2"
      >
        <div
          className="h-full bg-foreground transition-all"
          style={{ width: `${state.progress.percent}%` }}
        />
      </div>

      {expanded && (
        <ol className="divide-y divide-border">
          {ALL_STEPS.map((step) => {
            const meta = STEP_META[step];
            const isDone = done.has(step);
            const isNext = step === nextStep;
            return (
              <li key={step}>
                <Link
                  to={meta.href}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-surface-2 transition-colors ${
                    isNext ? 'bg-surface-2/40' : ''
                  }`}
                  data-testid={`checklist-step-${step}`}
                >
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 size-4 rounded-none flex items-center justify-center flex-shrink-0 ${
                      isDone
                        ? 'bg-foreground text-background'
                        : 'border border-border'
                    }`}
                  >
                    {isDone && <Check className="size-3" />}
                  </span>
                  <div className="flex flex-col min-w-0">
                    <span className={`text-sm ${isDone ? 'text-muted-foreground line-through' : 'text-foreground font-medium'}`}>
                      {meta.title}
                    </span>
                    {!isDone && (
                      <span className="text-xs text-muted-foreground">
                        {meta.description}
                      </span>
                    )}
                  </div>
                  {isNext && (
                    <span className="ml-auto self-center text-[10px] text-mono uppercase tracking-wider text-foreground flex-shrink-0">
                      Next
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ol>
      )}

      {/* DPA-Accept banner — separate from the 5 step list because it's
          a legal action, not an activation step. Hidden once accepted. */}
      {expanded && !state.dpaAccepted && (
        <div className="px-4 py-3 border-t border-border bg-surface-2/30">
          <div className="flex items-start gap-3">
            <FileSignature className="size-4 text-foreground flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium text-foreground">
                Review & accept the DPA
              </span>
              <span className="text-xs text-muted-foreground mb-2">
                Required before production traffic. Recorded in a separate cryptographic chain.
              </span>
              <button
                type="button"
                onClick={() => setDpaModalOpen(true)}
                data-testid="checklist-open-dpa-modal"
                className="adp-btn-secondary text-xs px-3 py-1.5 self-start min-h-[32px]"
              >
                Review agreement
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
    </>
  );
}
